import { FormEvent, useMemo, useState } from "react";
import {
  inventoryApi,
  type InventoryEditableProduct,
} from "../lib/inventory";
import { useRpcResource } from "../lib/useRpcResource";
import { ErrorPanel, Loading } from "./ResourceView";

const friendly = (value: string) => value.replaceAll("_", " ");

export function InventoryProductManager({
  organizationId,
  refreshKey = 0,
  onSaved,
}: {
  organizationId: string;
  refreshKey?: number;
  onSaved: () => Promise<void> | void;
}) {
  const resource = useRpcResource(
    () => inventoryApi.productEditor(organizationId),
    [organizationId, refreshKey],
  );
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [unit, setUnit] = useState("each");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("active");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const products = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (resource.data?.products ?? []).filter(
      (product) =>
        !q ||
        `${product.sku} ${product.name} ${product.category ?? ""} ${product.description ?? ""}`
          .toLowerCase()
          .includes(q),
    );
  }, [resource.data, search]);

  function edit(product: InventoryEditableProduct) {
    setEditingId(product.id);
    setSku(product.sku);
    setName(product.name);
    setCategory(product.category_id ?? "");
    setNewCategory("");
    setUnit(product.unit_of_measure || "each");
    setDescription(product.description ?? "");
    setStatus(product.status);
    setMessage(null);
    setError(null);
  }

  function cancel() {
    setEditingId(null);
    setError(null);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (
      !editingId ||
      !sku.trim() ||
      !name.trim() ||
      !category ||
      (category === "__new__" && !newCategory.trim())
    )
      return;
    setBusy(true);
    setMessage(null);
    setError(null);
    const result = await inventoryApi.updateProduct({
      organizationId,
      productId: editingId,
      sku: sku.trim(),
      name: name.trim(),
      categoryId: category === "__new__" ? null : category,
      newCategoryName:
        category === "__new__" ? newCategory.trim() : null,
      description: description.trim() || null,
      unitOfMeasure: unit.trim() || "each",
      status,
    });
    setBusy(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    setEditingId(null);
    setMessage("Product updated.");
    await resource.reload();
    await onSaved();
  }

  if (resource.loading && !resource.data)
    return (
      <section className="card inventory-product-card">
        <Loading />
      </section>
    );
  if (resource.error)
    return (
      <section className="card inventory-product-card">
        <h2>Current products</h2>
        <ErrorPanel message={resource.error} />
      </section>
    );
  if (!resource.data) return null;
  const data = resource.data;

  const canSave =
    Boolean(editingId && sku.trim() && name.trim() && category) &&
    (category !== "__new__" || Boolean(newCategory.trim()));

  return (
    <section className="card inventory-product-card">
      <div className="inventory-product-heading">
        <div>
          <h2>Current products</h2>
          <p className="muted">
            Edit the catalog information shared by every asset and tent assembly
            that uses the product.
          </p>
        </div>
        <label className="field inventory-product-search">
          <span>Find product</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, SKU or category"
          />
        </label>
      </div>
      {message && <div className="success-box">{message}</div>}
      {error && <ErrorPanel message={error} />}
      {editingId && (
        <form className="inventory-product-editor" onSubmit={submit}>
          <div className="inventory-product-editor-heading">
            <strong>Edit product</strong>
            <button type="button" onClick={cancel} disabled={busy}>
              Cancel
            </button>
          </div>
          <div className="form-grid four">
            <label className="field">
              <span>Part SKU</span>
              <input
                value={sku}
                onChange={(event) => setSku(event.target.value)}
                required
              />
            </label>
            <label className="field">
              <span>Part name</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </label>
            <label className="field">
              <span>Category</span>
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                required
              >
                <option value="">Select category</option>
                <option value="__new__">+ Create new category</option>
                {data.categories.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            {category === "__new__" ? (
              <label className="field">
                <span>New category name</span>
                <input
                  value={newCategory}
                  onChange={(event) => setNewCategory(event.target.value)}
                  required
                />
              </label>
            ) : (
              <label className="field">
                <span>Unit of measure</span>
                <input
                  value={unit}
                  onChange={(event) => setUnit(event.target.value)}
                />
              </label>
            )}
            {category === "__new__" && (
              <label className="field">
                <span>Unit of measure</span>
                <input
                  value={unit}
                  onChange={(event) => setUnit(event.target.value)}
                />
              </label>
            )}
            <label className="field">
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="discontinued">Discontinued</option>
                <option value="draft">Draft</option>
              </select>
            </label>
            <label className="field span-two">
              <span>Part description</span>
              <input
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </label>
          </div>
          <div className="inventory-product-editor-footer">
            <span>
              Tracking remains <strong>{friendly(data.products.find((p) => p.id === editingId)?.tracking_method ?? "none")}</strong> so existing inventory records stay connected.
            </span>
            <button className="primary" disabled={busy || !canSave}>
              {busy ? "Saving…" : "Save product"}
            </button>
          </div>
        </form>
      )}
      <div className="table-wrap inventory-scroll-list">
        <table className="data-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Category</th>
              <th>Tracking</th>
              <th>Unit</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id}>
                <td>
                  <strong>{product.name}</strong>
                  <small>{product.sku}</small>
                </td>
                <td>{product.category ?? "—"}</td>
                <td>
                  {friendly(product.tracking_method)}
                  <small>
                    {product.inventory_tracked
                      ? "Inventory tracked"
                      : "Not inventory tracked"}
                  </small>
                </td>
                <td>{product.unit_of_measure}</td>
                <td>
                  <span className={`status-pill status-${product.status}`}>
                    {friendly(product.status)}
                  </span>
                </td>
                <td>
                  <button
                    disabled={!data.capabilities.manage || busy}
                    onClick={() => edit(product)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && (
          <div className="empty-state">No products match this search.</div>
        )}
      </div>
    </section>
  );
}
